/**
 * Mobile Menu Drill-Down Navigation
 * Handles drill-down navigation in mobile menu
 * 
 * @requires ThemeUtils (utils.js) - optional
 */

class MobileMenuDrillDown {
  constructor() {
    this.mobileMenu = document.querySelector('.mobile-menu');
    this.navigationStack = [];
    this.init();
  }

  init() {
    if (!this.mobileMenu) return;

    this.bindEvents();
    this.setInitialState();
  }

  bindEvents() {
    // Event delegation for clicks on links with children
    this.mobileMenu.addEventListener('click', (e) => {
      const link = e.target.closest('[data-has-children]');
      if (link) {
        e.preventDefault();
        const submenuId = link.getAttribute('data-submenu');
        const title = link.querySelector('.mobile-menu__text')?.textContent || '';
        this.navigateToSubmenu(submenuId, title);
      }

      // Handle back buttons
      const backButton = e.target.closest('[data-back]');
      if (backButton) {
        e.preventDefault();
        this.navigateBack();
      }
    });

    // Reset when mobile menu closes
    const menuOverlay = document.getElementById('mobile-menu-overlay');
    if (menuOverlay) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
            const isOpen = menuOverlay.getAttribute('aria-hidden') === 'false';
            if (!isOpen) {
              this.reset();
            }
          }
        });
      });
      
      observer.observe(menuOverlay, { 
        attributes: true, 
        attributeFilter: ['aria-hidden'] 
      });
    }
  }

  /**
   * Set initial state - hide all submenus
   */
  setInitialState() {
    const submenus = this.mobileMenu.querySelectorAll('.mobile-menu__submenu');
    submenus.forEach(submenu => {
      submenu.classList.remove('active');
    });
    this.navigationStack = [];
  }

  /**
   * Navigate to a submenu
   * @param {string} submenuId - ID of the submenu to show
   * @param {string} title - Title to display in the submenu header
   */
  navigateToSubmenu(submenuId, title) {
    const submenu = document.getElementById(submenuId);
    if (!submenu) return;

    // Add to navigation stack
    this.navigationStack.push({ submenuId, title });

    // Hide main content if this is first level
    if (this.navigationStack.length === 1) {
      const mainContent = this.mobileMenu.querySelector('.mobile-menu__main-content');
      if (mainContent) {
        mainContent.classList.add('hidden');
      }
    }

    // Hide current menu if one is visible
    const currentMenu = this.getCurrentVisibleMenu();
    if (currentMenu) {
      currentMenu.classList.remove('active');
    }

    // Show new submenu
    submenu.classList.add('active');

    // Update header title if exists
    const headerTitle = submenu.querySelector('.mobile-menu__submenu-title');
    if (headerTitle) {
      headerTitle.textContent = title;
    }
  }

  /**
   * Navigate back to previous menu
   */
  navigateBack() {
    if (this.navigationStack.length === 0) return;

    // Remove from stack
    this.navigationStack.pop();

    // Hide current submenu
    const currentSubmenu = this.getCurrentVisibleMenu();
    if (currentSubmenu) {
      currentSubmenu.classList.remove('active');
    }

    // Show previous menu
    if (this.navigationStack.length > 0) {
      const previousMenu = this.navigationStack[this.navigationStack.length - 1];
      const previousSubmenu = document.getElementById(previousMenu.submenuId);
      if (previousSubmenu) {
        previousSubmenu.classList.add('active');
      }
    } else {
      // Return to main content
      const mainContent = this.mobileMenu.querySelector('.mobile-menu__main-content');
      if (mainContent) {
        mainContent.classList.remove('hidden');
      }
    }
  }

  /**
   * Get currently visible submenu
   * @returns {HTMLElement|null}
   */
  getCurrentVisibleMenu() {
    return this.mobileMenu.querySelector('.mobile-menu__submenu.active');
  }

  /**
   * Reset menu to initial state
   */
  reset() {
    this.navigationStack = [];

    // Hide all submenus
    const submenus = this.mobileMenu.querySelectorAll('.mobile-menu__submenu');
    submenus.forEach(submenu => {
      submenu.classList.remove('active');
    });

    // Show main content
    const mainContent = this.mobileMenu.querySelector('.mobile-menu__main-content');
    if (mainContent) {
      mainContent.classList.remove('hidden');
    }
  }

  /**
   * Public method to navigate programmatically
   * @param {string} submenuId - ID of the submenu
   * @param {string} title - Title for the submenu header
   */
  navigateTo(submenuId, title) {
    this.navigateToSubmenu(submenuId, title);
  }

  /**
   * Public method to return to main menu
   */
  goToMainMenu() {
    this.reset();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.mobileMenuDrillDown = new MobileMenuDrillDown();

  // Mobile menu carousel (touch swipe + dots)
  document.querySelectorAll('[data-carousel]').forEach(carousel => {
    const track = carousel.querySelector('.mobile-menu__carousel-track');
    const slides = carousel.querySelectorAll('.mobile-menu__carousel-slide');
    const dots = carousel.querySelectorAll('.mobile-menu__carousel-dot');
    if (!track || slides.length < 2) return;

    let current = 0;
    const gap = 12; // matches CSS gap

    function goTo(index) {
      if (index < 0) index = 0;
      if (index >= slides.length) index = slides.length - 1;
      current = index;
      const offset = slides[current].offsetLeft - track.offsetLeft;
      track.style.transform = `translateX(-${offset}px)`;
      dots.forEach((d, i) => d.classList.toggle('active', i === current));
    }

    // Dot clicks
    dots.forEach(dot => {
      dot.addEventListener('click', () => {
        goTo(parseInt(dot.getAttribute('data-carousel-dot'), 10));
      });
    });

    // Touch swipe
    let startX = 0;
    let startY = 0;
    let isDragging = false;
    let isHorizontal = null;

    track.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
      isHorizontal = null;
      track.style.transition = 'none';
    }, { passive: true });

    track.addEventListener('touchmove', e => {
      if (!isDragging) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;

      // Lock direction on first significant move
      if (isHorizontal === null && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
        isHorizontal = Math.abs(dx) > Math.abs(dy);
      }
      if (!isHorizontal) return;

      e.preventDefault();
      const offset = slides[current].offsetLeft - track.offsetLeft;
      track.style.transform = `translateX(-${offset - dx}px)`;
    }, { passive: false });

    track.addEventListener('touchend', e => {
      if (!isDragging) return;
      isDragging = false;
      track.style.transition = '';
      const dx = e.changedTouches[0].clientX - startX;
      if (isHorizontal) {
        if (dx < -40) goTo(current + 1);
        else if (dx > 40) goTo(current - 1);
        else goTo(current);
      }
    }, { passive: true });
  });
});